import { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertCircle, FileText, Printer, RefreshCw, X } from 'lucide-react';
import { formatPayrollMonth, PAYROLL_COMPANY } from './driverPayrollUtils';
import { fetchPayrollData } from './payrollApi';
import {
  computeDriverLedger,
  FLAT_TRIP_ALLOWANCE_AED,
} from './payrollCalculations';
import PayrollSkeleton from './PayrollSkeleton';

const TERMINAL_PAYROLL_KEY = 'terminal_payroll';

function loadPayrollFromStorage() {
  try {
    const raw = localStorage.getItem(TERMINAL_PAYROLL_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    return parsed;
  } catch (e) {
    console.error('[DriverPayroll] localStorage read failed:', e);
    return null;
  }
}

function savePayrollToStorage(payload) {
  try {
    localStorage.setItem(TERMINAL_PAYROLL_KEY, JSON.stringify(payload));
  } catch (e) {
    console.error('[DriverPayroll] localStorage write failed:', e);
  }
}

function mergePayrollDrivers(freshDrivers, storedDrivers) {
  if (!Array.isArray(storedDrivers) || !storedDrivers.length) return freshDrivers;

  return freshDrivers.map((driver) => {
    const saved = storedDrivers.find((row) => row.id === driver.id);
    return saved ? { ...driver, ...saved } : driver;
  });
}

/** Inline palette — overrides global shell via style attributes */
const P = {
  silver: '#F1F5F9',
  white: '#FFFFFF',
  border: '#CBD5E1',
  grid: '#E2E8F0',
  blue: '#3B82F6',
  blueHover: '#2563EB',
  text: '#1E293B',
  muted: '#64748B',
  earn: '#059669',
  deduct: '#DC2626',
};

const lightRoot = {
  backgroundColor: P.silver,
  color: P.text,
  colorScheme: 'light',
};

const lightCard = {
  backgroundColor: P.white,
  borderColor: P.grid,
};

const lightSilverInset = {
  backgroundColor: P.silver,
  borderColor: P.border,
};

function LedgerRow({ label, sublabel, amount, amountTone = 'neutral' }) {
  const amountColor =
    amountTone === 'earn'
      ? P.earn
      : amountTone === 'ot'
        ? P.blue
        : amountTone === 'deduct'
          ? P.deduct
          : P.text;

  return (
    <li
      className="flex items-center justify-between gap-4 rounded-xl border px-5 py-4 shadow-sm"
      style={{ ...lightCard, borderWidth: 1, borderStyle: 'solid' }}
    >
      <div className="min-w-0">
        <p className="text-sm font-bold" style={{ color: P.text }}>
          {label}
        </p>
        {sublabel && (
          <p
            className="mt-1 text-[10px] font-bold uppercase tracking-wide"
            style={{ color: P.muted }}
          >
            {sublabel}
          </p>
        )}
      </div>
      <p
        className="shrink-0 font-mono text-xl font-extrabold tabular-nums"
        style={{ color: amountColor }}
      >
        {amount}
      </p>
    </li>
  );
}

function PayslipModal({ open, driver, ledger, monthLabel, onClose, onPrint }) {
  if (!open || !driver || !ledger) return null;

  const slipId = `PS-${driver.id}-202606`;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(241, 245, 249, 0.92)', colorScheme: 'light' }}
      role="dialog"
      aria-modal="true"
    >
      <button
        type="button"
        aria-label="Close payslip"
        className="absolute inset-0 cursor-pointer border-none"
        style={{ backgroundColor: 'transparent' }}
        onClick={onClose}
      />
      <div
        className="relative max-h-[90vh] w-full max-w-lg overflow-hidden rounded-2xl border shadow-xl"
        style={{ ...lightCard, borderColor: P.border, borderWidth: 1, borderStyle: 'solid' }}
      >
        <div
          className="flex items-center justify-between border-b px-5 py-3"
          style={{
            backgroundColor: P.silver,
            borderColor: P.grid,
            borderBottomWidth: 1,
            borderBottomStyle: 'solid',
          }}
        >
          <span className="flex items-center gap-2 text-sm font-bold" style={{ color: P.text }}>
            <FileText className="h-4 w-4" style={{ color: P.blue }} />
            Official Payslip
          </span>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onPrint}
              className="inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-bold"
              style={{ backgroundColor: P.blue, color: P.white }}
            >
              <Printer className="h-3.5 w-3.5" />
              Print / PDF
            </button>
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border p-1.5"
              style={{
                backgroundColor: P.white,
                borderColor: P.border,
                borderWidth: 1,
                borderStyle: 'solid',
                color: P.muted,
              }}
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
        <div
          id="payroll-slip-print"
          className="overflow-y-auto p-8"
          style={{ backgroundColor: P.white, color: P.text }}
        >
          <h2 className="text-lg font-bold" style={{ color: P.text }}>
            {PAYROLL_COMPANY.name}
          </h2>
          <p className="text-xs" style={{ color: P.muted }}>
            {slipId} · {monthLabel}
          </p>
          <p className="mt-4 font-bold" style={{ color: P.text }}>
            {driver.name} ({driver.id})
          </p>
          <table className="mt-6 w-full text-sm">
            <tbody>
              {[
                ['Basic Salary', `${ledger.baseSalary} AED`],
                [`Trip Incentives (${ledger.tripsCompleted} drops)`, `${ledger.tripIncentives} AED`],
                [`Overtime (${ledger.otHours} hrs)`, `${ledger.overtimePay} AED`],
                ['Penalties', `−${ledger.penalties} AED`],
              ].map(([label, val]) => (
                <tr key={label} style={{ borderBottom: `1px solid ${P.grid}` }}>
                  <td className="py-2.5 font-medium" style={{ color: P.text }}>
                    {label}
                  </td>
                  <td className="py-2.5 text-right font-mono font-bold" style={{ color: P.text }}>
                    {val}
                  </td>
                </tr>
              ))}
              <tr>
                <td className="py-4 text-base font-extrabold" style={{ color: P.text }}>
                  Net Payout
                </td>
                <td
                  className="py-4 text-right font-mono text-lg font-extrabold"
                  style={{ color: P.blue }}
                >
                  {ledger.netPayout} AED
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function PayrollErrorState({ message, onRetry }) {
  return (
    <div
      className="flex min-h-[320px] flex-col items-center justify-center rounded-2xl border p-10 text-center"
      style={{ ...lightRoot, borderColor: P.grid, borderWidth: 1, borderStyle: 'solid' }}
      role="alert"
    >
      <div
        className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl"
        style={{ backgroundColor: '#FEE2E2', color: P.deduct }}
      >
        <AlertCircle className="h-7 w-7" />
      </div>
      <h2 className="text-lg font-bold" style={{ color: P.text }}>
        Payroll Sync Failed
      </h2>
      <p className="mt-2 max-w-md text-sm" style={{ color: P.muted }}>
        {message || 'Unable to reach the dispatch payroll database. Please retry.'}
      </p>
      <button
        type="button"
        onClick={onRetry}
        className="mt-6 inline-flex items-center gap-2 rounded-xl px-5 py-3 text-sm font-bold text-white shadow-md"
        style={{ backgroundColor: P.blue }}
      >
        <RefreshCw className="h-4 w-4" />
        Retry Sync
      </button>
    </div>
  );
}

export default function DriverPayroll() {
  const [viewYear] = useState(2026);
  const [viewMonth] = useState(5);

  const [drivers, setDrivers] = useState(() => loadPayrollFromStorage()?.drivers || []);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [monthLabel, setMonthLabel] = useState(() => {
    const cached = loadPayrollFromStorage();
    return cached?.monthLabel || formatPayrollMonth(viewYear, viewMonth);
  });
  const [selectedDriverId, setSelectedDriverId] = useState(
    () => loadPayrollFromStorage()?.selectedDriverId || '',
  );
  const [slipOpen, setSlipOpen] = useState(false);

  const loadPayroll = useCallback(
    async (signal) => {
      setLoading(true);
      setError(null);

      try {
        const payload = await fetchPayrollData({ year: viewYear, month: viewMonth, signal });
        const cached = loadPayrollFromStorage();
        const mergedDrivers = mergePayrollDrivers(payload.drivers, cached?.drivers);

        setDrivers(mergedDrivers);
        setMonthLabel(payload.monthLabel);
        setSelectedDriverId((prev) => {
          const preferred = prev || cached?.selectedDriverId || '';
          if (preferred && mergedDrivers.some((d) => d.id === preferred)) return preferred;
          return mergedDrivers[0]?.id || '';
        });
      } catch (err) {
        if (err?.name === 'AbortError') return;
        setError(err?.message || 'Payroll database connection failed.');
        const cached = loadPayrollFromStorage();
        if (!cached?.drivers?.length) setDrivers([]);
      } finally {
        if (!signal?.aborted) setLoading(false);
      }
    },
    [viewYear, viewMonth],
  );

  useEffect(() => {
    const controller = new AbortController();
    loadPayroll(controller.signal);
    return () => controller.abort();
  }, [loadPayroll]);

  useEffect(() => {
    if (!drivers.length) return;

    savePayrollToStorage({
      drivers,
      selectedDriverId,
      monthLabel,
      viewYear,
      viewMonth,
      savedAt: new Date().toISOString(),
    });
  }, [drivers, selectedDriverId, monthLabel, viewYear, viewMonth]);

  const selectedDriver = useMemo(
    () => drivers.find((d) => d.id === selectedDriverId) || drivers[0] || null,
    [drivers, selectedDriverId],
  );

  const ledger = useMemo(
    () => (selectedDriver ? computeDriverLedger(selectedDriver) : null),
    [selectedDriver],
  );

  const handleDriverChange = (e) => {
    setSelectedDriverId(e.target.value);
  };

  const handlePrintClick = () => {
    setSlipOpen(true);
  };

  const handleRetry = () => {
    loadPayroll();
  };

  const handlePrint = () => {
    const node = document.getElementById('payroll-slip-print');
    if (!node) return;
    const w = window.open('', '_blank', 'width=900,height=700');
    if (!w) return;
    w.document.write(
      `<!DOCTYPE html><html><head><title>Payslip — ${selectedDriver?.name}</title><style>body{background:#FFFFFF;color:#1E293B;font-family:sans-serif;padding:24px;}</style></head><body>${node.innerHTML}</body></html>`,
    );
    w.document.close();
    w.focus();
    w.print();
  };

  if (loading) return <PayrollSkeleton />;

  if (error) {
    return <PayrollErrorState message={error} onRetry={handleRetry} />;
  }

  if (!ledger || !selectedDriver) {
    return (
      <div
        className="rounded-2xl p-10 text-center text-sm font-semibold"
        style={{ ...lightRoot, color: P.muted }}
      >
        No driver payroll records found for this period.
      </div>
    );
  }

  return (
    <div className="w-full font-sans">
      <header
        className="mb-8 flex flex-col gap-4 border-b-2 pb-6 md:flex-row md:items-center md:justify-between"
        style={{ borderColor: P.border, borderBottomStyle: 'solid' }}
      >
        <div>
          <span
            className="inline-block rounded-full border px-3 py-1 text-[10px] font-bold uppercase tracking-widest"
            style={{
              backgroundColor: P.white,
              borderColor: P.grid,
              borderWidth: 1,
              borderStyle: 'solid',
              color: P.blue,
            }}
          >
            Logistics Fleet · Live API Feed
          </span>
          <h1
            className="mt-2 text-2xl font-black tracking-tight sm:text-3xl"
            style={{ color: P.text }}
          >
            DISPATCH PAYROLL LEDGER
          </h1>
          <p
            className="mt-1 text-xs font-bold uppercase tracking-wider"
            style={{ color: P.muted }}
          >
            {monthLabel} · {drivers.length} drivers synced · Daylight-readable
          </p>
        </div>

        <div
          className="flex items-center gap-3 rounded-xl border px-4 py-3 shadow-sm"
          style={{ ...lightCard, borderColor: P.border, borderWidth: 1, borderStyle: 'solid' }}
        >
          <label
            htmlFor="driver-select"
            className="text-[10px] font-bold uppercase tracking-wider"
            style={{ color: P.muted }}
          >
            Active Driver
          </label>
          <select
            id="driver-select"
            value={selectedDriver.id}
            onChange={handleDriverChange}
            className="cursor-pointer rounded-lg border px-3 py-2 text-sm font-bold outline-none focus:ring-2"
            style={{
              backgroundColor: P.silver,
              borderColor: P.grid,
              borderWidth: 1,
              borderStyle: 'solid',
              color: P.blue,
              colorScheme: 'light',
            }}
          >
            {drivers.map((x) => (
              <option key={x.id} value={x.id} style={{ backgroundColor: P.white, color: P.text }}>
                {x.name} ({x.id})
              </option>
            ))}
          </select>
        </div>
      </header>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <section
          className="lg:col-span-2 rounded-2xl border p-6 shadow-sm"
          style={{ ...lightCard, borderWidth: 1, borderStyle: 'solid' }}
        >
          <div
            className="mb-5 flex items-center gap-2 border-b pb-4"
            style={{ borderColor: P.grid, borderBottomWidth: 1, borderBottomStyle: 'solid' }}
          >
            <span
              className="flex h-9 w-9 items-center justify-center rounded-lg text-lg"
              style={{
                backgroundColor: P.silver,
                boxShadow: `inset 0 0 0 1px ${P.grid}`,
              }}
            >
              📋
            </span>
            <div>
              <h2 className="text-base font-extrabold" style={{ color: P.text }}>
                Automated Earnings Matrix
              </h2>
              <p
                className="text-[10px] font-bold uppercase tracking-wide"
                style={{ color: P.muted }}
              >
                Basic · Trips · OT · Penalties
              </p>
            </div>
          </div>

          <ul className="space-y-3" aria-label="Payroll line items">
            <LedgerRow
              label="Contract Basic Salary"
              sublabel="Fixed pillar · employment bond"
              amount={`${ledger.baseSalary.toLocaleString()} AED`}
              amountTone="neutral"
            />
            <LedgerRow
              label="Automated Trip Incentives"
              sublabel={`${ledger.tripsCompleted} verified drops × ${FLAT_TRIP_ALLOWANCE_AED} AED`}
              amount={`+${ledger.tripIncentives.toLocaleString()} AED`}
              amountTone="earn"
            />
            <LedgerRow
              label="Calculated Overtime (1.5×)"
              sublabel={`${ledger.otHours} hrs × ${ledger.overtimeRate} AED/hr baseline`}
              amount={`+${ledger.overtimePay.toLocaleString()} AED`}
              amountTone="ot"
            />
            <LedgerRow
              label="System Enforced Penalties"
              sublabel="Fleet safety guard · geofence compliance"
              amount={`−${ledger.penalties.toLocaleString()} AED`}
              amountTone="deduct"
            />
          </ul>
        </section>

        <aside
          className="flex flex-col justify-between rounded-2xl border p-6 shadow-sm"
          style={{ ...lightCard, borderWidth: 1, borderStyle: 'solid' }}
        >
          <div>
            <h2
              className="border-b pb-3 text-base font-extrabold"
              style={{
                color: P.text,
                borderColor: P.grid,
                borderBottomWidth: 1,
                borderBottomStyle: 'solid',
              }}
            >
              Net Settlement
            </h2>

            <div
              className="mt-6 rounded-2xl border-2 border-dashed px-4 py-8 text-center"
              style={{ ...lightSilverInset, borderStyle: 'dashed', borderWidth: 2 }}
            >
              <p
                className="text-[10px] font-bold uppercase tracking-widest"
                style={{ color: P.muted }}
              >
                Final Disbursable Net
              </p>
              <p
                className="mt-2 font-mono text-4xl font-black tabular-nums"
                style={{ color: P.text }}
              >
                {ledger.netPayout.toLocaleString()}
                <span className="ml-1 text-lg font-bold" style={{ color: P.blue }}>
                  AED
                </span>
              </p>
              <p className="mt-2 text-[11px] font-semibold" style={{ color: P.muted }}>
                {ledger.name} · {ledger.id}
              </p>
            </div>

            <dl
              className="mt-5 space-y-2 border-t pt-4 text-xs"
              style={{ borderColor: P.grid, borderTopWidth: 1, borderTopStyle: 'solid' }}
            >
              <div className="flex justify-between font-medium" style={{ color: P.muted }}>
                <dt>Gross components</dt>
                <dd className="font-mono font-bold" style={{ color: P.text }}>
                  {(ledger.baseSalary + ledger.tripIncentives + ledger.overtimePay).toLocaleString()}{' '}
                  AED
                </dd>
              </div>
              <div className="flex justify-between font-medium" style={{ color: P.muted }}>
                <dt>Deductions</dt>
                <dd className="font-mono font-bold" style={{ color: P.deduct }}>
                  −{ledger.penalties.toLocaleString()} AED
                </dd>
              </div>
            </dl>
          </div>

          <button
            type="button"
            onClick={handlePrintClick}
            className="mt-6 w-full rounded-xl py-4 text-sm font-black uppercase tracking-wide shadow-md transition focus:outline-none focus:ring-2 focus:ring-offset-2"
            style={{
              backgroundColor: P.blue,
              color: P.white,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = P.blueHover;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = P.blue;
            }}
          >
            📄 Print Official Payslip
          </button>
        </aside>
      </div>

      <PayslipModal
        open={slipOpen}
        driver={selectedDriver}
        ledger={ledger}
        monthLabel={monthLabel}
        onClose={() => setSlipOpen(false)}
        onPrint={handlePrint}
      />
    </div>
  );
}
